import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * 검색해서 고르는 선택 입력 (IPT-613).
 *
 * 후보가 수십 개인 필드(유해차단 예외항목(상세) 48개)용. `<select>`는 스크롤로 찾아야 하고
 * `<datalist>`는 브라우저가 그리는 자동완성이라 스타일도, 열림 여부도 손댈 수 없다.
 * → 목록을 직접 그려 검색·키보드 조작·선택 표시를 갖춘다.
 *
 * **자유 입력을 막지 않는다.** 목록에 없는 값도 그대로 값이 된다 —
 * 운영 DB에 후보 밖 값이 남아 있어(예: '임시 웹페이지'), 막으면 기존 행 수정 시 값이 지워진다.
 */

export interface SearchableSelectProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/** 검색어와 일치하는 구간을 굵게 — 왜 이 항목이 남았는지 눈으로 보이게 한다 */
const Highlighted: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const at = text.toLowerCase().indexOf(query.toLowerCase());
  if (at < 0) return <>{text}</>;

  return (
    <>
      {text.slice(0, at)}
      <mark className="bg-transparent font-semibold text-primary">
        {text.slice(at, at + query.length)}
      </mark>
      {text.slice(at + query.length)}
    </>
  );
};

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  // 열려 있을 때만 검색어로 좁힌다. 닫혀 있으면 입력창은 선택된 값을 보여준다.
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : [...options];
  }, [options, query]);

  const close = React.useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  /** 열 때는 지금 값에서 시작한다 — 목록이 길어 매번 처음부터 훑지 않도록 */
  const open = React.useCallback(() => {
    setIsOpen((wasOpen) => {
      if (!wasOpen) setActiveIndex(options.indexOf(value));
      return true;
    });
  }, [options, value]);

  // 바깥 클릭으로 닫기 — 폼 안에 있어 모달 오버레이 훅(useOverlayDismiss)은 쓸 수 없다
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isOpen, close]);

  // 키보드로 옮긴 항목이 스크롤 밖으로 나가지 않게
  // (scrollIntoView가 없는 환경 — jsdom 등 — 에서도 조작 자체는 계속돼야 한다)
  React.useEffect(() => {
    if (activeIndex < 0) return;
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  const select = (option: string) => {
    onChange(option);
    setQuery('');
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      if (filtered.length === 0) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((i) => {
        const next = i + delta;
        if (next < 0) return filtered.length - 1;
        if (next >= filtered.length) return 0;
        return next;
      });
      return;
    }
    if (e.key === 'Enter' && isOpen && activeIndex >= 0 && filtered[activeIndex]) {
      e.preventDefault();
      select(filtered[activeIndex]);
    }
  };

  const optionId = (index: number) => `${id}-option-${index}`;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div className="relative">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${id}-listbox`}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          autoComplete="off"
          disabled={disabled}
          value={isOpen ? query : value}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value); // 목록 밖 값도 그대로 값이 된다
            setIsOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={open}
          onClick={open}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-background pl-3 text-sm text-foreground',
            value && !isOpen ? 'pr-14' : 'pr-8',
            'ring-offset-background placeholder:text-muted-foreground transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
        {/* 고른 값 지우기 — 잘못 고른 뒤 전체 선택해 지우는 수고를 덜어준다 */}
        {value && !isOpen && !disabled && (
          <button
            type="button"
            aria-label="선택 지우기"
            onClick={() => onChange('')}
            className={cn(
              'absolute right-7 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full',
              'text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            )}
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3 w-3">
              <path d="m5 5 10 10M15 5 5 15" strokeLinecap="round" />
            </svg>
          </button>
        )}

        {/* 펼침 표시 — 목록이 열려 있으면 뒤집힌다 */}
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          className={cn(
            'pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-transform',
            isOpen && 'rotate-180',
          )}
        >
          <path d="M5 7.5 10 12.5 15 7.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      {isOpen && (
        <div
          className={cn(
            'absolute z-50 mt-1.5 w-full overflow-hidden rounded-lg border border-border',
            'bg-card shadow-lg shadow-black/5 ring-1 ring-black/[0.03]',
          )}
        >
          <ul
            ref={listRef}
            id={`${id}-listbox`}
            role="listbox"
            className={cn(
              'max-h-72 overflow-y-auto overscroll-contain py-1',
              // 기본 스크롤바는 목록 폭을 잡아먹고 투박하다 — 얇게 깎는다
              '[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent',
              '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border',
            )}
          >
            {filtered.map((option, index) => {
              const isActive = index === activeIndex;
              const isSelected = option === value;
              return (
                <li
                  key={option}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(e) => e.preventDefault()} // 입력창 포커스 유지
                  onClick={() => select(option)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-sm transition-colors',
                    isActive ? 'bg-accent text-accent-foreground' : 'text-foreground',
                    isSelected && 'font-medium',
                  )}
                >
                  <span className="truncate">
                    <Highlighted text={option} query={query.trim()} />
                  </span>
                  {isSelected && (
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="h-3.5 w-3.5 shrink-0 text-primary"
                    >
                      <path d="m5 10.5 3.5 3.5L15 6.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </li>
              );
            })}
          </ul>

          {filtered.length === 0 && (
            <p className="px-3 py-2.5 text-xs text-muted-foreground" style={{ wordBreak: 'keep-all' }}>
              일치하는 항목이 없습니다 — 입력한 값을 그대로 사용합니다.
            </p>
          )}

          {filtered.length > 0 && (
            <div
              className={cn(
                'flex items-center justify-between gap-2 border-t border-border bg-muted/40 px-3 py-1.5',
                'text-[11px] text-muted-foreground',
              )}
            >
              {/* 좁은 칼럼에서도 한 줄로 — 줄바꿈되면 목록이 아니라 안내가 눈에 먼저 든다 */}
              <span className="whitespace-nowrap">
                {query.trim() ? `${filtered.length}개 일치` : `전체 ${filtered.length}개`}
              </span>
              <span className="truncate opacity-70">↑↓ Enter Esc</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

SearchableSelect.displayName = 'SearchableSelect';
